/**
 * TenantNeutralGate — enforces the tenant-neutral contract of
 * app.systemdox.com (#553).
 *
 * Per specs/archive/tenant-url-structure, the neutral hosts serve only
 * login, signup, /join and the tenant redirect — never the console UI.
 * The AppRoutes redirect effect relies on the token's tenantSlug claim,
 * which the published shared-react-auth User type does not expose yet
 * (#493), so in practice it never fires and the console stayed fully
 * usable on the neutral hosts. This gate resolves the tenant the same
 * way the workspace badge does — GET /auth/v1/tenants — and keeps the
 * console behind a spinner until the browser is on the tenant console
 * host.
 *
 * Interim behaviour until app.systemdox.com becomes the capture app
 * (#554): once that ships, the gate moves with the console routes.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { LoadingState } from '@systemdox/ui';
import { useAuthContext } from '@/contexts/AuthContext';
import { tenantApi, type TenantMembership } from '@/services/tenantApi';
import { isAppDomain, launchUrl } from '@/utils/subdomain';
import { isLeaving } from '@/lib/returnTo';
import { loadPendingInvite } from '@/utils/pendingInvite';
import { shouldShowWelcome } from '@/utils/welcome';

export type GateDecision =
  | { action: 'allow' }
  | { action: 'redirect'; slug: string }
  | { action: 'resolve' };

/**
 * Decide what a tenant-neutral host does with an authenticated user.
 * Exported for tests; the component wires it to the live inputs.
 *
 * - Off-neutral hosts, pending invites (#495) and the one-time welcome
 *   (#496) pass through — AppRoutes owns those navigations and they
 *   must resolve on this origin.
 * - A resolvable tenant slug (token claim, else the active membership)
 *   redirects to the tenant root host (its whole app lives there now).
 * - `memberships === null` means "not fetched yet" → resolve first.
 * - Fetched but no active slug (no workspace yet, or the membership
 *   API failed) fails open: stranding the user on a spinner is worse
 *   than the neutral host briefly serving the shell.
 */
export function decideNeutralGate(input: {
  neutralHost: boolean;
  pendingInvite: boolean;
  welcomeDue: boolean;
  claimSlug: string | null;
  memberships: TenantMembership[] | null;
}): GateDecision {
  if (!input.neutralHost || input.pendingInvite || input.welcomeDue) {
    return { action: 'allow' };
  }
  if (input.claimSlug) return { action: 'redirect', slug: input.claimSlug };
  if (input.memberships === null) return { action: 'resolve' };
  const slug = input.memberships.find((m) => m.active)?.slug;
  return slug ? { action: 'redirect', slug } : { action: 'allow' };
}

export function TenantNeutralGate({ children }: { children: ReactNode }) {
  const { user } = useAuthContext();
  const [memberships, setMemberships] = useState<TenantMembership[] | null>(null);

  const decision = decideNeutralGate({
    neutralHost: isAppDomain(),
    pendingInvite: loadPendingInvite() !== null,
    welcomeDue: shouldShowWelcome(user),
    claimSlug: (user as { tenantSlug?: string } | null)?.tenantSlug ?? null,
    memberships,
  });

  useEffect(() => {
    if (decision.action !== 'resolve') return;
    let cancelled = false;
    tenantApi
      .listTenants()
      .then((tenants) => {
        if (!cancelled) setMemberships(tenants);
      })
      .catch(() => {
        if (!cancelled) setMemberships([]); // fail open, see decideNeutralGate
      });
    return () => {
      cancelled = true;
    };
  }, [decision.action]);

  const redirectSlug = decision.action === 'redirect' ? decision.slug : null;
  // The sign-in that just landed is already taking the person back to the
  // page they came from on their workspace (LoginPage's `?redirect=`, e.g. a
  // step-up from Settings › API keys): a replace now would cancel that return.
  const leaving = isLeaving();
  useEffect(() => {
    if (!redirectSlug || leaving) return;
    const { pathname, search, hash } = window.location;
    // The tenant's whole app lives on its root host now — send them there.
    window.location.replace(launchUrl(redirectSlug, `${pathname}${search}${hash}`));
  }, [redirectSlug, leaving]);

  if (decision.action === 'allow' && !leaving) return <>{children}</>;

  return (
    <LoadingState
      size="lg"
      label="Taking you to your workspace…"
      className="h-screen bg-slate-50 dark:bg-slate-950"
    />
  );
}
